import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Spin } from "antd";
import { useOutbox } from "@dowty/offline";
import { AccountDataPanel } from "@dowty/legal";
import { useTranslation } from "react-i18next";
import { AuthUser } from "../../../context/AuthContext";
import { cancelAccountDeletion, exportMyData, getDeletionStatus, requestAccountDeletion } from "../../../services/userService";
import { fetchDrinkActivities } from "../../../services/drinks/DrinkService";
import { buildDrinksCsv, buildDrinksJson } from "../../../services/drinks/drinkExport";
import { outbox, reads } from "../../../offline";

const MAX_DRINK_EXPORT_ROWS = 10_000;

export const AccountDataSection: React.FC<{ user: AuthUser }> = ({ user }) => {
    const { t, i18n } = useTranslation();
    const [scheduledFor, setScheduledFor] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [hasLoadedStatus, setHasLoadedStatus] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);
    const [exportEmpty, setExportEmpty] = useState(false);
    const [exportSuccess, setExportSuccess] = useState(false);
    const { entries } = useOutbox(outbox);
    const language = (i18n.resolvedLanguage ?? i18n.language ?? "en").toLowerCase().startsWith("de") ? "de" : "en";

    const downloadDrinks = async (format: "csv" | "json") => {
        setExportLoading(true);
        setExportError(null);
        setExportEmpty(false);
        setExportSuccess(false);
        try {
            const activities = await fetchDrinkActivities(user);
            if (!activities.length) {
                setExportEmpty(true);
                return;
            }
            if (activities.length > MAX_DRINK_EXPORT_ROWS) {
                setExportError("profile.accountData.drinksExportTooLarge");
                return;
            }

            const date = new Date();
            const today = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
            const content = format === "csv" ? `\uFEFF${buildDrinksCsv(activities)}` : buildDrinksJson(activities);
            const blob = new Blob([content], { type: format === "csv" ? "text/csv;charset=utf-8" : "application/json;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `saufdings-drinks-${today}.${format}`;
            link.click();
            window.setTimeout(() => URL.revokeObjectURL(url), 0);
            setExportSuccess(true);
        } catch {
            setExportError("profile.accountData.drinksExportError");
        } finally {
            setExportLoading(false);
        }
    };

    const loadStatus = useCallback(async (showLoading = false) => {
        if (showLoading) setLoading(true);
        setLoadError(false);
        try {
            setScheduledFor(await getDeletionStatus(user));
            setHasLoadedStatus(true);
        } catch {
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        void loadStatus(true);
    }, [loadStatus]);

    if (loading) {
        return (
            <section className="profile-account-data-state" aria-labelledby="profile-account-data-heading" aria-busy="true">
                <div>
                    <h2 id="profile-account-data-heading">{t("profile.accountData.title")}</h2>
                    <p>{t("profile.accountData.loading")}</p>
                </div>
                <Spin size="small" />
            </section>
        );
    }

    if (loadError && !hasLoadedStatus) {
        return (
            <section className="profile-account-data-state" aria-labelledby="profile-account-data-heading">
                <h2 id="profile-account-data-heading">{t("profile.accountData.title")}</h2>
                <Alert
                    type="error"
                    showIcon
                    message={t("profile.accountData.loadError")}
                    action={<Button onClick={() => void loadStatus(true)}>{t("profile.accountData.retry")}</Button>}
                />
            </section>
        );
    }

    return <>
        {entries.length > 0 && (
            <Alert
                type="warning"
                showIcon
                message={t("profile.accountData.pendingWarning", { count: entries.length })}
            />
        )}
        {loadError && (
            <Alert
                type="error"
                showIcon
                message={t("profile.accountData.loadError")}
                action={<Button onClick={() => void loadStatus()}>{t("profile.accountData.retry")}</Button>}
            />
        )}
        <AccountDataPanel
            language={language}
            deletionScheduledFor={scheduledFor}
            onExport={() => exportMyData(user)}
            onRequestDeletion={async (password) => {
                await requestAccountDeletion(user, password);
                await Promise.all([reads.clear(), outbox.clear()]);
            }}
            onCancelDeletion={() => cancelAccountDeletion(user)}
            onChanged={() => void loadStatus()}
        />
        <section aria-label={t("profile.accountData.drinksExportTitle")} aria-busy={exportLoading}>
            <h3>{t("profile.accountData.drinksExportTitle")}</h3>
            <p>{t("profile.accountData.drinksExportDescription")}</p>
            <Button disabled={exportLoading} loading={exportLoading} style={{ minHeight: 44 }} onClick={() => void downloadDrinks("csv")}>
                {t("profile.accountData.drinksExportCsv")}
            </Button>{" "}
            <Button disabled={exportLoading} loading={exportLoading} style={{ minHeight: 44 }} onClick={() => void downloadDrinks("json")}>
                {t("profile.accountData.drinksExportJson")}
            </Button>
            {exportLoading && <output>{t("profile.accountData.drinksExportLoading")}</output>}
            {exportEmpty && <output>{t("profile.accountData.drinksExportEmpty")}</output>}
            {exportSuccess && <output>{t("profile.accountData.drinksExportSuccess")}</output>}
            {exportError && <Alert type="error" showIcon role="alert" message={t(exportError)} />}
        </section>
    </>;
};
